/*
 * This code is the intellectual property of Bulb Inc. and other software publishers;
 * it may not be altered, copied or disclosed without prior written approval.
 * Copyright 2021, Bulb Inc. For more information, please email contact@hellobulb.com.
 *          _
 *  _            | | _
 * | |         _ | || |
 * | |__   _  | || || |__
 * | '_ \ | | | || || '_ \
 * | (_) || |_| || || (_) |
 *  \___/  \___/ |_| \___/
*/

/**
 * @module bulb-block-id/bulbblockidediting
 */
import { Plugin, ViewElement, ViewDowncastWriter } from 'ckeditor5';
import type {
  ModelWriter, Editor, ModelElement, DowncastConversionApi, EventInfo,
} from 'ckeditor5';

import { forEach } from 'lodash-es';
import CKEditorUtils from '../../utils';

export const blockIdKey = 'bulb-id';
export const tableAlignment = 'tableAlignment';
export const paragraph = 'paragraph';
export const fontFamily = 'fontFamily';

/**
 * The block id plugin. It sets id for all the elements when they are inserted.
 *
 * @extends Plugin
 */
export default class BulbBlockIdEditing extends Plugin {

  /**
   * @inheritDoc
   */
  public static get pluginName() {
    return 'BulbBlockIdEditing' as const;
  }

  /**
   * @inheritDoc
   */
  public init(): void {
    const { editor } = this;
    // Extending schema to allow our custom attribute on elements.
    editor.model.schema.extend('tableRow', { allowAttributes: blockIdKey });
    editor.model.schema.extend('tableCell', { allowAttributes: blockIdKey });

    editor.conversion.attributeToAttribute({
      model: blockIdKey,
      view: blockIdKey,
    });
    const elementNames = ['paragraph', 'table', 'blockQuote', 'heading2', 'heading3'];
    forEach(elementNames, (blockType) => {
      editor.model.schema.extend(blockType, { allowAttributes: blockIdKey });
      editor.conversion.for('downcast')
        .add((dispatcher) => {
          dispatcher.on(`insert:${blockType}`, (evt: EventInfo, data, conversionApi: DowncastConversionApi) => {
            const viewWriter = conversionApi.writer;
            let modelElement = data.item;
            if (blockType === 'table') {
              const viewFigure = conversionApi.mapper.toViewElement(modelElement);
              BulbBlockIdEditing.addStyleToTable(modelElement, viewFigure, editor);
              BulbBlockIdEditing.addIdToTable(modelElement, viewFigure, conversionApi, viewWriter, editor);
            } else {
              let viewElement = conversionApi.mapper.toViewElement(modelElement);
              if (blockType === 'paragraph' && data.item.parent !== null && data.item.parent.name === 'tableCell') {
                modelElement = data.item.parent;
                viewElement = conversionApi.mapper.toViewElement(modelElement);
              }

              let id = viewElement.getAttribute(blockIdKey) || data.item.getAttribute(blockIdKey);
              if (!id) {
                BulbBlockIdEditing.preserveFontFamily(editor);
                id = CKEditorUtils.mongoObjectId();
                BulbBlockIdEditing.updateModelElement(editor, modelElement, id);
                viewWriter.setAttribute(blockIdKey, id, viewElement);
              }
            }
          }, { priority: 'low' });
        });
    });

    // To prevent the value being copied to next element on enter.
    editor.commands.get('enter')
      .on('afterExecute', () => {
        const block = Array.from(editor.model.document.selection.getSelectedBlocks())[0];
        editor.model.change((writer) => {
          if (block) {
            writer.removeAttribute(blockIdKey, block);
          }
        });
      });
  }

  /**
   * Use to preserve last paragraph font-family after any asset insertion.
   *
   * @param editor editor instance.
   */
  private static preserveFontFamily(editor) {
    let nodes: string | any[];
    let lastFontFamily: any;
    const modelDoc = editor.model.document;
    const changes = modelDoc.differ.getChanges()[0].position;
    if (changes) {
      const { nodeBefore } = changes;
      const nextSibling = changes.nodeAfter?.nextSibling;
      // Need to handle the 'horizontalLine scenario' differently.
      const isDividerElement = changes.nodeAfter?.name === 'horizontalLine';
      const allElements = modelDoc.selection.getLastPosition()?.root?._children?._nodes;
      const onlyParagraphElements = allElements.filter((node) => node.name === paragraph).reverse();
      /* Here used index[1] to get the second last paragraph node because at [0] we have our current new
       * paragraph inserted.
       */
      const lastParagraphNode = onlyParagraphElements[1];

      if (((nodeBefore && nodeBefore.name !== paragraph && !nextSibling)
        || (isDividerElement)) && lastParagraphNode) {
        nodes = lastParagraphNode._children?._nodes;
      }
      if (nextSibling && !changes.isAtStart) {
        if (nodeBefore.name !== paragraph && lastParagraphNode && nextSibling.name !== paragraph) {
          nodes = lastParagraphNode._children?._nodes;
        } else if (nextSibling.name === paragraph && !isDividerElement) {
          nodes = nextSibling._children?._nodes;
        } else if (!isDividerElement) {
          nodes = nodeBefore._children?._nodes;
        }
      }

      // Execute fontFamily command
      if (nodes && nodes.length > 0) {
        lastFontFamily = nodes[nodes.length - 1].getAttribute(fontFamily);
        if (lastFontFamily) {
          editor.execute(fontFamily, { value: lastFontFamily });
        }
      }
    }
  }

  /**
   * Use to add margin-right to table on left align.
   *
   * @param {module:engine/model/element~ModelElement} modelElement
   * @param viewFigure current viewFigure in the editor.
   * @param editor editor instance.
   */
  private static addStyleToTable(modelElement: ModelElement, viewFigure: ViewElement, editor: Editor): void {
    const align = viewFigure.getAttribute(tableAlignment) || modelElement.getAttribute(tableAlignment);
    if (align === 'left') {
      editor.model.change((writer: ModelWriter) => {
        writer.setAttribute('style', 'margin-right: 15px;', modelElement);
      });
      const floatClass = document.querySelectorAll('figure.table.ck-widget[style="float:left;"]');
      if (floatClass.length !== 0) {
        const element = floatClass[0] as HTMLElement;
        element.style.cssText = element.style.cssText.concat('margin-right:15px');
      }
    }
  }

  /**
   * Use to find the table element inside figure.
   *
   * @param {module:engine/view/element~ViewElement} viewElement
   * @param viewElementName : Name of child element inside figure element which has to be returned.
   * @param {module:engine/conversion/downcastdispatcher~DowncastConversionApi} conversionApi
   * @returns The table element which is child of figure.
   */
  private static findViewChild(
    viewElement: ViewElement,
    viewElementName: string,
    conversionApi: DowncastConversionApi,
  ) {
    const viewChildren = Array.from(conversionApi.writer.createRangeIn(viewElement).getItems());
    return viewChildren.find((item: ViewElement) => (item).is('element', viewElementName));
  }

  /**
   * Method that will add id to table element.
   *
   * @param {module:engine/model/element~ModelElement} modelElement
   * @param viewFigure current viewFigure in the editor.
   * @param {module:engine/conversion/downcastdispatcher~DowncastConversionApi} conversionApi
   * @param viewWriter editor conversion writer.
   * @param editor editor instance.
   */
  private static addIdToTable(
    modelElement: ModelElement,
    viewFigure: ViewElement,
    conversionApi: DowncastConversionApi,
    viewWriter: ViewDowncastWriter,
    editor,
  ) {
    const viewElement = BulbBlockIdEditing.findViewChild(viewFigure, 'table', conversionApi);
    const element = viewElement as ViewElement;
    let id = element.getAttribute(blockIdKey) || modelElement.getAttribute(blockIdKey);
    if (!id) {
      id = CKEditorUtils.mongoObjectId();
      editor?.bulb?.service.ckeStateService.insertParagraphSubject.next(true);
      editor?.bulb?.service.ckeStateService.insertParagraphAfterTableSubject.next(modelElement);
    }
    BulbBlockIdEditing.updateModelElement(editor, modelElement, id);
    viewWriter.setAttribute(blockIdKey, id, element);
    BulbBlockIdEditing.addIdToTableRow(modelElement.getChildren(), conversionApi, viewWriter, editor);
  }

  /**
   * Method that will add id to table row element.
   *
   * @param children Child elements of table element.
   * @param {module:engine/conversion/downcastdispatcher~DowncastConversionApi} conversionApi
   * @param viewWriter editor conversion writer.
   * @param editor editor instance.
   */
  private static addIdToTableRow(
    children,
    conversionApi: DowncastConversionApi,
    viewWriter: ViewDowncastWriter,
    editor: Editor,
  ) {
    forEach(Array.from(children), (childElement) => {
      const element = childElement as ModelElement;
      if (element.name === 'tableRow') {
        const viewElement = conversionApi.mapper.toViewElement(element);
        let id = viewElement.getAttribute(blockIdKey) || element.getAttribute(blockIdKey);
        if (!id) {
          id = CKEditorUtils.mongoObjectId();
          BulbBlockIdEditing.updateModelElement(editor, element, id);
          viewWriter.setAttribute(blockIdKey, id, viewElement);
        }
      }
    });
  }

  /**
   * Add the attribute to the model element.
   *
   * @param editor editor instance.
   * @param modelElement Model ModelElement
   * @param id Id of element
   */
  private static updateModelElement(editor: Editor, modelElement: ModelElement, id): void {
    editor.model.change((writer: ModelWriter) => {
      writer.setAttribute(blockIdKey, id, modelElement);
    });
  }
}
